<template>
  <standard-dialog
    v-model="model"
    :title="formTitle"
    :subtitle="editedItem?.id ? 'ID: ' + editedItem.id : ''"
    icon="mdi-truck-fast"
    @save="save"
    :max-width="800"
    :loading="loading"
    :disabled="!valid"
  >
    <v-form v-model="valid">
      <v-container style="padding-bottom: 10px !important">
        <v-row>
          <v-col cols="12" md="6" lg="4">
            <text-field
              v-model="editedItem.name"
              :rules="[rules.required]"
              :label="$t('title')"
              required
            />
          </v-col>
          <v-col v-if="companies.length" cols="12" md="6" lg="4">
            <autocomplete
              v-model="editedItem.parent"
              :items="
                [{ name: '', id: null }].concat(companies.filter((item) => item.id))
              "
              :label="$t('parent_company')"
              item-text="name"
              item-id="id"
              return-object
            />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <autocomplete
              v-model="editedItem.active"
              :items="statusOptions"
              item-text="name"
              item-value="val"
              :label="$t('status')"
            />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <!--
              Country is UI-only, used to filter the city dropdown below - it is
              never sent to the server. country_id is not a real column on
              delivery_companies (confirmed 12.09.2026 backend answer, B2).
            -->
            <autocomplete
              @change="onCountryChange"
              v-model="editedItem.country"
              :items="countries"
              :label="$t('country')"
              item-text="name"
              item-id="id"
              return-object
            />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <autocomplete
              v-model="editedItem.city"
              @change="onCityChange"
              :items="cities ? cities : []"
              :label="$t('city')"
              item-text="name"
              item-id="id"
              return-object
            />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <text-field v-model="editedItem.phone" :label="$t('phone')" />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <text-field
              v-model="editedItem.email"
              :label="$t('email')"
              required
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <text-field v-model="editedItem.jib" :label="$t('jib')" />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <text-field v-model="editedItem.pib" :label="$t('pib')" />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <text-field v-model="editedItem.owner" :label="$t('owner')" />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <text-field
              v-model="editedItem.contact_person"
              :label="$t('contact_person')"
            />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <text-field v-model="editedItem.contract" :label="$t('contract')" />
          </v-col>
          <v-col cols="12">
            <!--
              delivery_companies.note - present (always null so far) on every
              GET row we've inspected, just never exposed in this form or sent
              on save. Testing 13.09 whether POST/PUT actually persist it.
            -->
            <text-field v-model="editedItem.note" :label="$t('note')" />
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <autocomplete
              v-model="companyCurrency"
              :items="currencyOptions"
              item-text="text"
              item-value="val"
              :label="$t('select_currency')"
            />
          </v-col>
          <!--
            paying_type/paying (firm -> Ordera platform fee) are hidden from
            the restaurant role on GET as of the 15.09.2026 backend answer -
            a restaurant opening this dialog would see them as null and, on
            save, overwrite the real value with null. Restricted to admin
            here to match, since admin/admin_delivery are the only roles the
            backend still returns these two fields to.
          -->
          <template v-if="isAdmin">
            <v-col cols="12" md="6" lg="4">
              <autocomplete
                v-model="editedItem.paying_type"
                :items="platformPayingTypeOptions"
                item-text="name"
                item-value="val"
                :label="$t('platform_pay_type')"
              />
            </v-col>
            <v-col cols="12" md="6" lg="4">
              <text-field
                v-model="editedItem.paying"
                :label="$t('amount')"
                :suffix="platformPaymentSuffix"
              />
            </v-col>
          </template>
          <v-col cols="12" md="6" lg="4">
            <!--
              Pure UI flag, confirmed 15.09.2026 backend answer (N2) - safe to
              expose now, unlike the rest of the extended company schema.
            -->
            <v-checkbox
              v-model="editedItem.show_price_breakdown"
              :label="$t('show_price_breakdown')"
              color="primary"
              hide-details
            />
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasModel from "@/mixins/hasModel";
import { deliveryCompany } from "@/models/company";
import companyService from "@/services/company";
import { CURRENCY_OPTIONS, parseSettings } from "@/utils/currency";

export default {
  name: "DeliveryCompanyDialog",
  mixins: [hasModel, hasValidation, hasSnackbar],
  components: {
    StandardDialog,
  },
  props: {
    editedItem: {
      type: Object,
      default: () => ({ ...deliveryCompany }),
    },
    companies: {
      type: Array,
      default: () => [],
    },
    countries: {
      type: Array,
      default: () => [],
    },
    cities: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.user.user.type === "admin";
    },
    formTitle() {
      return this.editedItem?.id
        ? this.$t("edit_delivery_company")
        : this.$t("add_delivery_company");
    },
    currencyOptions() {
      return CURRENCY_OPTIONS;
    },
    // delivery_companies.active: 0-neaktivna, 1-aktivna, 2-obrisana (confirmed
    // 12.09.2026 backend answer).
    statusOptions() {
      return [
        { name: this.$t("inactive"), val: 0 },
        { name: this.$t("active"), val: 1 },
        { name: this.$t("deleted"), val: 2 },
      ];
    },
    // delivery_companies.paying_type/paying - how the FIRM pays Ordera for
    // platform use (distinct from restaurant_delivery_company.paying_type,
    // labeled "pay_type", and from delivery_company_user.paying_type, labeled
    // "courier_pay_type" - three separate relations, confirmed 12.09.2026).
    platformPayingTypeOptions() {
      return [
        { name: this.$t("by_month"), val: 1 },
        { name: this.$t("by_percent"), val: 2 },
        { name: this.$t("by_meal"), val: 3 },
        { name: this.$t("by_delivery"), val: 4 },
      ];
    },
    // "By percent" isn't a currency amount - only by_month/by_meal/by_delivery
    // are. Same pattern as RestaurantOwnDeliverySettingsDialog.paymentCurrency.
    platformPaymentSuffix() {
      if (Number(this.editedItem.paying_type) === 2) return "%";
      return this.companyCurrency || "KM";
    },
    // Delivery company currency lives inside the free-form `settings` JSON, under `currency`.
    companyCurrency: {
      get() {
        return parseSettings(this.editedItem.settings).currency || null;
      },
      set(value) {
        const settings = parseSettings(this.editedItem.settings);
        settings.currency = value;
        this.$set(this.editedItem, "settings", settings);
      },
    },
  },
  methods: {
    onCountryChange() {
      this.$emit("country-change");
    },
    onCityChange() {
      this.$emit("city-change");
    },
    async save() {
      try {
        this.editedItem.parent_id = this.editedItem.parent
          ? this.editedItem.parent.id
          : 0;
        this.loading = true;

        // editedItem is a deep copy of a GET table row, so it carries nested
        // relations (parent company object, delivery_company_user,
        // restaurant_delivery_company) that must never be sent back to the
        // server — build the request body from only the fields this form edits.
        // country_id/latitude/longitude/address are NOT real columns on
        // delivery_companies (confirmed 12.09.2026 backend answer, B2) and are
        // intentionally left out here - `country` stays a client-only filter
        // for the city dropdown above.
        const payload = {
          name: this.editedItem.name,
          email: this.editedItem.email,
          parent_id: this.editedItem.parent_id,
          city_id: this.editedItem.city ? this.editedItem.city.id : this.editedItem.city_id,
          phone: this.editedItem.phone,
          jib: this.editedItem.jib,
          pib: this.editedItem.pib,
          owner: this.editedItem.owner,
          contact_person: this.editedItem.contact_person,
          contract: this.editedItem.contract,
          settings: this.editedItem.settings,
          note: this.editedItem.note,
          active: this.editedItem.active,
          show_price_breakdown: this.editedItem.show_price_breakdown,
        };

        // Only an admin ever sees/edits these (see the template comment above) -
        // never send them for a restaurant user, whose editedItem has them as
        // null/undefined because the backend hides them on GET for that role.
        // Sending them unconditionally would overwrite the real value with null.
        if (this.isAdmin) {
          payload.paying_type = this.editedItem.paying_type;
          payload.paying = this.editedItem.paying;
        }

        if (this.editedItem.id) {
          // Update existing company
          await companyService.updateDeliveryCompany(this.editedItem.id, payload);
          this.showSuccessSnackbar(this.$t("delivery_company_updated"));
          this.$emit("company-updated", {
            item: this.editedItem,
          });
        } else {
          // Create new company
          const res = await companyService.createDeliveryCompany(payload);
          this.editedItem.id = res.data.id;
          this.showSuccessSnackbar(this.$t("delivery_company_created"));
          this.$emit("company-created", this.editedItem);
        }
        this.close();
      } catch (error) {
        // paying_type/paying validation (e.g. an out-of-range paying_type)
        // now returns 422 with a message instead of a 500 - confirmed
        // 15.09.2026 backend answer (N1). Surface it instead of the generic
        // message, same pattern as RestaurantSettingsDialog.
        if (error.response?.status === 422 && error.response?.data?.message) {
          this.showErrorSnackbar(error.response.data.message);
        } else {
          this.showErrorSnackbar(this.$t("error_saving_delivery_company"));
        }
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.model = false;
      this.$emit("close");
    },
  },
};
</script>
